// BCM-898 · PA4 — Material batch expired (slides 11, 12). [Uday]
// Status 37. Two tabs: "Material Batch Expired" (default, badge) and "View
// Consumption Document"; top-right Sync Inventory / Sync PO / Resubmit Document.
// The failure detail is a pink card with horizontal labelled fields (mockup slide 11).
// Sync Inventory here triggers recheck + reset (V5).
//
// PA4 is also reached from status 55: the workflow writes 'Stock Check Not
// Required' only when stockCheckRequired is false, so when stock IS checked the
// batch stays on 'Post Manufacturing Digitalized Data Validated' while Step 7's
// expiry loop runs, and the pending action there is this one (see the resolver's
// case for POST_MFG_VALIDATED).
//
// FIELD SOURCE. checkConsumedBatchExpiry (goodsReceiptApi) —
// workflow/checkForConsumptionBatchExpiry, called with the EXACT same request
// shape Step 7 itself sends (temporal-workflows/.../checkExpiredConsumptionBatchesActivity.ts
// + its call site in processFlow1Workflow.ts Step 7 — both verified against the
// currently-live workflow code, not the middleware team's not-yet-deployed
// simplified contract):
//   Request:  { schrockenDeliveryScheduleBatchID, collaboratorTypeID:1,
//               GRNValidation:false, IBDValidation:false, scheduleAgreementNumber,
//               batchDetails: {vendorMosymphonyID, brandOwnerMosymphonyID,
//               vendorBatchNumber, scheduleAgreementNumber, BomConsumptionDetails},
//               skipNotification:true }
//     IBDValidation/GRNValidation are BOTH false here — Step 7 is the plain
//     expiry check every flow runs the same way; the IBD/GRN-aware storage-
//     location validation is a DIFFERENT call site (the shipment gate, PA8's
//     domain), not this one. productConfig is omitted: the controller only
//     reads it inside the notification-prep block, which skipNotification:true
//     skips entirely (njm-corda-pcmp/.../checkForConsumptionBatchExpiry.js).
//   Response: { status, message, hasExpiredBatches,
//               data: { batchExpired, consumptionDetails:[{materialCode,
//               componentBatchNumber, isExpired, expiryDate}] } } — the RAW
//     controller response (same shape checkExpiredConsumptionBatchesActivity.ts
//     itself reads via `data?.data?.consumptionDetails`). Dates arrive as raw
//     ISO strings, not pre-formatted — formatted client-side same as the A6
//     fallback path.
// batchDetails.BomConsumptionDetails is built from A2 `detail.bomConsumptionDetails`
// (lowercase on the A2 response — confirmed against
// FetchDeliveryScheduleBatchDetailsFlow.java; the controller's own field is
// capitalized, hence the rename on the way out).
// Fallback: A6 (fetchComponentAllocationPlannedAndActualNewDirect), used only
// when the primary call can't be made (detail not loaded yet) or fails (network
// error, unreachable in this deployment, etc.).
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import SectionShell from "../components/SectionShell";
import ActionTabs from "../components/common/ActionTabs";
import FieldCard from "../components/common/FieldCard";
import ConsumptionDocumentTab from "../components/common/ConsumptionDocumentTab";
import SyncActions from "../components/common/SyncActions";
import ResubmitDocumentButton from "../components/common/ResubmitDocumentButton";
import { fetchPlannedVsActual, checkConsumedBatchExpiry } from "../api/goodsReceiptApi";
import { isExpired, daysElapsedSince, formatDate, trimQty } from "../utils/formatters";

// A6 fallback shape → the render-ready internal shape (see below).
const collectExpired = (data) => {
  const out = [];
  // data (res.data.data) is the component array directly — no nested
  // componentAllocationList property (confirmed against VarianceMismatchDialog.jsx
  // and ApproveRejectConsumedBatch.jsx, the other working callers of this endpoint).
  (Array.isArray(data) ? data : []).forEach((comp) => {
    (comp.actualConsumptionDetails || []).forEach((c) => {
      if (isExpired(c.expiryDate)) {
        const days = daysElapsedSince(c.expiryDate);
        out.push({
          batchNumber: c.componentBatchNumber,
          expiryDateText: `${formatDate(c.expiryDate)}${days != null ? ` (expired ${days} days ago)` : ""}`,
          consumedQty: c.BomComponentConsumedQuantity,
          uom: comp.uom,
        });
      }
    });
  });
  return out;
};

// The workflow check's own consumptionDetails[] → the render-ready internal
// shape. Only carries materialCode/componentBatchNumber/isExpired/expiryDate —
// quantity/UOM come from the same batchDetails.BomConsumptionDetails the request
// itself was built from (merged in here, same pattern as the A6 fallback).
const collectExpiredFromWorkflowCheck = (consumptionDetails, bomConsumptionDetails) => {
  const qtyByBatch = {};
  (Array.isArray(bomConsumptionDetails) ? bomConsumptionDetails : []).forEach((comp) => {
    (comp.consumptionBatchNumbers || []).forEach((b) => {
      qtyByBatch[b.componentBatchNumber] = {
        consumedQty: b.BomComponentConsumedQuantity,
        uom: comp.BomComponentRefUOM,
      };
    });
  });
  return (Array.isArray(consumptionDetails) ? consumptionDetails : [])
    .filter((c) => c && c.isExpired)
    .map((c) => {
      const days = daysElapsedSince(c.expiryDate);
      return {
        batchNumber: c.componentBatchNumber,
        expiryDateText: `${formatDate(c.expiryDate)}${days != null ? ` (expired ${days} days ago)` : ""}`,
        ...(qtyByBatch[c.componentBatchNumber] || {}),
      };
    });
};

const PA4MaterialExpired = ({ row, detail, featuresAccess, viewOnly, onRefresh }) => {
  const [expired, setExpired] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const cancelRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving PA4 material expired");
    };
  }, []);

  useEffect(() => {
    if (!row || !row.schrockenDeliveryScheduleBatchID) return;
    setLoading(true);
    setError("");
    if (cancelRef.current) cancelRef.current.cancel("superseded PA4 fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;

    // fetchComponentAllocationPlannedAndActualNewDirect/{batchID}/{batchSize} —
    // batchSize, not poQuantity/targetQuantity/batchQty (a different number;
    // the endpoint returns an empty componentAllocationList against those).
    const batchSize = row.batchSize || row.poQuantity || row.targetQuantity || row.batchQty || 0;
    const fallbackToA6 = () =>
      fetchPlannedVsActual(row.schrockenDeliveryScheduleBatchID, batchSize, source.token)
        .then((res) => {
          if (!mountedRef.current) return;
          if (res.data && res.data.status) setExpired(collectExpired(res.data.data));
          else setError((res.data && res.data.message) || "Could not load consumption data.");
          setLoading(false);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          if (!mountedRef.current) return;
          setError((err.response && err.response.data && err.response.data.message) || err.message);
          setLoading(false);
        });

    // A2's own Corda flow (FetchDeliveryScheduleBatchDetailsFlow.java) puts this
    // under the LOWERCASE key "bomConsumptionDetails" — confirmed by reading the
    // flow itself, not assumed. Do not "fix" the casing back to BomConsumptionDetails.
    const bomConsumptionDetails = detail && Array.isArray(detail.bomConsumptionDetails)
      ? detail.bomConsumptionDetails
      : null;

    if (!bomConsumptionDetails) {
      // Detail hasn't loaded (or doesn't carry this field) — go straight to A6.
      fallbackToA6();
      return;
    }

    checkConsumedBatchExpiry(
      {
        schrockenDeliveryScheduleBatchID: Number(row.schrockenDeliveryScheduleBatchID),
        collaboratorTypeID: 1,
        // Step 7 always calls the plain expiry check this way — IBD/GRN storage-
        // location validation is a different call site (the shipment gate).
        IBDValidation: false,
        GRNValidation: false,
        scheduleAgreementNumber: row.scheduleAgreementNumber,
        batchDetails: {
          vendorMosymphonyID: row.vendorMosymphonyID,
          brandOwnerMosymphonyID: row.brandOwnerMosymphonyID,
          vendorBatchNumber: row.vendorBatchNumber,
          scheduleAgreementNumber: row.scheduleAgreementNumber,
          BomConsumptionDetails: bomConsumptionDetails,
        }
      },
      source.token
    )
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) {
          setExpired(
            collectExpiredFromWorkflowCheck(
              res.data.data && res.data.data.consumptionDetails,
              bomConsumptionDetails
            )
          );
          setLoading(false);
        } else {
          fallbackToA6();
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        // Workflow endpoint unreachable/erroring in this environment — degrade to
        // the A6-derived approach rather than showing an error.
        fallbackToA6();
      });
  }, [row, detail]);

  const failTab = () => (
    <React.Fragment>
      <Typography style={{ color: "#6B7280", fontSize: "0.85rem", marginBottom: 12 }}>
        The consumed material batch reported for this batch has expired. Resolve the
        failure below and re-run the check.
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}><CircularProgress size={22} /></Box>
      ) : error ? (
        <Typography variant="body2" style={{ color: "#C0392B" }}>{error}</Typography>
      ) : expired.length === 0 ? (
        <Typography variant="body2" style={{ color: "#777" }}>
          No expired consumed batches found. Sync Inventory if you have updated the
          expiry date in SAP.
        </Typography>
      ) : (
        expired.map((e, i) => (
          <FieldCard
            key={`${e.batchNumber}-${i}`}
            fields={[
              { label: "Consumed Material Batch Number", value: e.batchNumber },
              { label: "Expiry Date", value: e.expiryDateText },
              { label: "Consumed Material Qty", value: `${trimQty(e.consumedQty)} ${e.uom || ""}` },
            ]}
          />
        ))
      )}
    </React.Fragment>
  );

  return (
    <SectionShell viewOnly={viewOnly}>
      <ActionTabs
        headRight={
          <React.Fragment>
            <SyncActions
              row={row}
              detail={detail}
              featuresAccess={featuresAccess}
              showSyncInventory
              showSyncPO
              resetsBatchInventory
              resetsBatchPO
              onRefresh={onRefresh}
            />
            <ResubmitDocumentButton row={row} detail={detail} featuresAccess={featuresAccess} onAfter={onRefresh} blue />
          </React.Fragment>
        }
        tabs={[
          { key: "expired", label: "Material Batch Expired", badge: expired.length || null, render: failTab },
          {
            key: "doc",
            label: "View Consumption Document",
            render: () => (
              <ConsumptionDocumentTab row={row} detail={detail} featuresAccess={featuresAccess} onRefresh={onRefresh} />
            ),
          },
        ]}
      />
    </SectionShell>
  );
};

export default PA4MaterialExpired;
